import { engineComponent, ArrayUtils, StringUtils } from "neo-common";
import { BaseElement } from "./BaseElement";
import { ContainerElement } from "./ContainerElement";
import { SwitchStyle, SwitchStyleCaseAttributes } from "../Styles/SwitchStyle";
import { StyleManager } from "../../Managers/StyleManager";
import { AnimationElement } from "./AnimationElement";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../../Utils/FrameTimer";
import { ComponentTypes } from "../../ComponentTypes";
import { ParticleElement } from "./ParticleElement";
import { ProtonElement } from "./ProtonElement";
import { ShapeElement } from "./ShapeElement";
import { Tweens } from "../../Effects/Tweens";

/**
 * SwitchElement class is class which creates holder for other elements and holds cases which can change children by switching cases.
 * Can be defined only with SwitchStyle (See SwitchStyle class).
 * Default case is NONE.
 * Example 1:
 *   <SwitchElement Class="SwitchElement" Style="SwitchStyle" />
 *
 * @class SwitchElement
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class SwitchElement extends BaseElement {
    // event for dispatch end of movie clip
    static AnimationComplete: string = "animationComplete";
    static FinishAnimation: string = "finishAnimation";

    // style is mandatory in this element
    protected style: SwitchStyle;
    protected case: string;

    protected elements: ContainerElement;

    protected timer: FrameTimerEventEmitter;

    // TODO clean
    center: boolean;
    // symbol width without offsetX
    private _symbolWidth: number;
    // symbol height without offsetY
    private _symbolHeight: number;

    private dynamicTime = 0;

    constructor() {
        super();

        // default case
        this.case = "NONE";
        this.center = false;

        this.timer = CreateFrameTimer("timer");
    }

    public get symbolWidth(): number {
        return this._symbolWidth;
    }

    public get symbolHeight(): number {
        return this._symbolHeight;
    }

    public set symbolWidth(value: number) {
        this._symbolWidth = value;
    }

    public set symbolHeight(value: number) {
        this._symbolHeight = value;
    }

    public get hasElements(): boolean {
        return this.elements != null && this.elements.childs != null;
    }

    public getAllChildElements(): Object {
        if (this.hasElements) {
            return this.elements.childs;
        }

        return null;
    }

    public getChildElementByName(name: string): Object {
        if (this.hasElements) {
            return this.elements.childs[name];
        }

        return null;
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    createByNode(node: Element) {
        super.createByNode(node);

        if (node.hasAttribute(SwitchElementAttributes.STYLE)) {
            this.style = <SwitchStyle>StyleManager.Styles[node.getAttribute(SwitchElementAttributes.STYLE)];
            if (this.style != null) {
                // get all elements and add them to this display list
                if (this.style.container != null) {
                    if (
                        node.hasAttribute(SwitchElementAttributes.CLONE) &&
                        StringUtils.parseBoolean(node.getAttribute(SwitchElementAttributes.CLONE))
                    ) {
                        this.elements = this.style.container.clone();
                    } else {
                        this.elements = this.style.container;
                    }
                    this.addChild(this.elements);
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no style with name: " + node.getAttribute(SwitchElementAttributes.STYLE));
                }
            }
        }

        if (node.hasAttribute(SwitchElementAttributes.CASE)) {
            this.goToCase(node.getAttribute(SwitchElementAttributes.CASE));
        }
    }

    updateByNode(node: Element) {
        super.updateByNode(node);

        this.dynamicTime = 0;
        if (node.hasAttribute(SwitchElementAttributes.DYNAMIC_TIME)) {
            this.setDynamicTime(parseInt(node.getAttribute(SwitchElementAttributes.DYNAMIC_TIME)));
        }

        if (node.hasAttribute(SwitchElementAttributes.CASE)) {
            this.goToCase(node.getAttribute(SwitchElementAttributes.CASE));
        } else if (node.hasAttribute(SwitchElementAttributes.RANDOM_CASE)) {
            this.goToCase(this.getRandomCase(node.getAttribute(SwitchElementAttributes.RANDOM_CASE)));
        }
    }

    /**
     * Find out if this element has case s.
     * @method hasCase
     * @param s name of case
     * @return Boolean
     */
    hasCase(s: string): Boolean {
        return this.style != null && this.style.cases != null && this.style.cases[s];
    }

    /**
     * Returns xml element of case s.
     * @method getCase
     * @param s name of case
     * @return Element
     */
    getCase(s: string): Element {
        if (this.style != null && this.style.cases != null && this.style.cases[s]) {
            return this.style.cases[s];
        }
        return null;
    }

    public getCurrentCase(): string {
        return this.case;
    }

    /**
     * Switch this elemenet to case s.
     * @method goToCase
     * @param s name of case
     * @param emitEvent boolean
     * @return void
     */
    goToCase(s: string, emitEvent: boolean = false): void {
        if (emitEvent) {
            this.animationComplete();
        }

        if (this.style != null && this.style.cases != null && this.case !== s) {
            if (this.timer != null) {
                this.timer.removeAllListeners(FrameTimerEvents.Complete);
                this.timer.stop();
            }

            if (this.hasCase(s)) {
                let node: Element = this.style.cases[s];
                if (node != null) {
                    if (DEBUG) {
                        if (node.hasAttribute("Debug") && node.getAttribute("Debug") === "true") {
                            debugger;
                        }
                    }

                    if (node.hasAttribute("FinishHandler")) {
                        this.emit(SwitchElement.FinishAnimation);
                    }

                    let element: any;
                    this.case = s;
                    let next: Element = node.firstElementChild;
                    let arr: Array<string> = [];
                    while (next != null) {
                        arr.push(next.nodeName);
                        element = this.elements.childs[next.nodeName];
                        if (element != null) {
                            element.visible = true;
                            // send new node to element and set / change new attributes
                            element.updateByNode(next);
                        } else {
                            if (DEBUG) {
                                this.log(
                                    "WARNING no element child with name: " +
                                        next.nodeName +
                                        " in case: " +
                                        s +
                                        " in style: " +
                                        this.style.name,
                                );
                            }
                        }
                        next = next.nextElementSibling;
                    }

                    for (let key in this.elements.childs) {
                        let child: any = this.elements.childs[key];
                        child.removeAllListeners();

                        let found: boolean = false;
                        for (let i: number = 0; i < arr.length; i++) {
                            if (key === arr[i]) {
                                found = true;
                            }
                        }
                        if (!found) {
                            // ToDo Implement other Graphics elements?
                            if (child instanceof ParticleElement) {
                                (child as any as ParticleElement).stop();
                            } else if (child instanceof SwitchElement) {
                                child.visible = false;
                                child.goToCase("EMPTY");
                            } else if (child instanceof PIXI.DisplayObject) {
                                child.visible = false;
                            }

                            if (child instanceof ProtonElement) {
                                (child as any as ProtonElement).stop();
                            }

                            Tweens.removeTweens(child);
                        }
                    }

                    let nextCase: string = null;
                    if (node.hasAttribute(SwitchStyleCaseAttributes.NEXT_CASE)) {
                        nextCase = node.getAttribute(SwitchStyleCaseAttributes.NEXT_CASE);
                    } else if (node.hasAttribute(SwitchStyleCaseAttributes.NEXT_RANDOM_CASE)) {
                        nextCase = this.getRandomCase(node.getAttribute(SwitchStyleCaseAttributes.NEXT_RANDOM_CASE));
                    }

                    if (nextCase != null && this.hasCase(nextCase)) {
                        if (node.hasAttribute(SwitchStyleCaseAttributes.TIMER)) {
                            let multiplier = 1;
                            if (node.hasAttribute(SwitchElementAttributes.RANDOM)) {
                                multiplier = this.getRandomDurationMultiplier(node);
                            }

                            this.timer.on(FrameTimerEvents.Complete, this.goToCase.bind(this, nextCase));
                            if (node.getAttribute(SwitchStyleCaseAttributes.TIMER) === "dynamic") {
                                this.timer.start(this.dynamicTime * multiplier);
                            } else {
                                this.timer.start(
                                    parseInt(node.getAttribute(SwitchStyleCaseAttributes.TIMER)) * multiplier,
                                );
                            }
                        } else if (node.hasAttribute(SwitchStyleCaseAttributes.HANDLER)) {
                            element = this.elements.childs[node.getAttribute(SwitchStyleCaseAttributes.HANDLER)];
                            if (element) {
                                element.on(
                                    AnimationElement.AnimationComplete,
                                    this.goToCase.bind(this, nextCase, true),
                                );
                            }
                        } else {
                            this.goToCase(nextCase);
                        }
                    } else {
                        if (node.hasAttribute(SwitchStyleCaseAttributes.TIMER)) {
                            let multiplier = 1;
                            if (node.hasAttribute(SwitchElementAttributes.RANDOM)) {
                                multiplier = this.getRandomDurationMultiplier(node);
                            }

                            this.timer.on(FrameTimerEvents.Complete, this.animationComplete.bind(this));
                            if (node.getAttribute(SwitchStyleCaseAttributes.TIMER) === "dynamic") {
                                this.timer.start(this.dynamicTime * multiplier);
                            } else {
                                this.timer.start(
                                    parseInt(node.getAttribute(SwitchStyleCaseAttributes.TIMER)) * multiplier,
                                );
                            }
                        } else if (node.hasAttribute(SwitchStyleCaseAttributes.HANDLER)) {
                            element = this.elements.childs[node.getAttribute(SwitchStyleCaseAttributes.HANDLER)];
                            if (element) {
                                element.on(AnimationElement.AnimationComplete, this.animationComplete.bind(this));
                            }
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no case: " + s + " for " + this.name);
                }
            }
        }

        if (emitEvent) {
            this.animationComplete();
        }
    }

    /**
     * Returns random multiplier from array of multipliers of attribute Random="multiplierMIN;multiplierMAX"
     * @param {Element} node
     * @returns {number}
     */
    private getRandomDurationMultiplier(node: Element): number {
        let result = 1;

        let dataArray = ArrayUtils.numberArray(node.getAttribute(SwitchElementAttributes.RANDOM));
        if (dataArray.length > 0) {
            let max = dataArray[0];
            let min = 1;

            if (dataArray.length === 2) {
                min = dataArray[0];
                max = min > dataArray[1] ? min : dataArray[1];
            }

            result = Math.round(SwitchElement.getRandom(min, max));
        }

        return result;
    }

    /**
     * Get random case from array of cases.
     * @returns {string}
     * @param s
     */
    private getRandomCase(s: string): string {
        let result = 0;

        let dataArray: Array<string> = ArrayUtils.stringArray(s);
        if (dataArray.length > 0) {
            let max = dataArray.length - 1;
            let min = 0;

            result = Math.round(SwitchElement.getRandom(min, max));
        } else {
            return null;
        }

        return dataArray[result];
    }

    /**
     * Set time of timer dynamicaly from code.
     * @param {number} time
     * @returns {SwitchElement}
     */
    public setDynamicTime(time: number): void {
        this.dynamicTime = time;
    }

    /**
     *
     * @param {number} min
     * @param {number} max
     * @returns {number}
     */
    private static getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    /**
     * Emit complete event when finish changing state.
     * @method animationComplete
     * @return void
     */
    animationComplete(): void {
        this.emit(SwitchElement.AnimationComplete);
    }

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return SwitchElement
     */
    clone(): SwitchElement {
        const element: SwitchElement = new SwitchElement();

        element.name = this.name;
        element.style = this.style;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.visible = this.visible;

        element.elements = this.elements.clone();
        element.addChild(element.elements);

        element.case = this.case;

        if (this.maskSprite != null) {
            let elementMask = new PIXI.Sprite((<PIXI.Sprite>this.maskSprite).texture);
            elementMask.position.x = this.maskSprite.position.x;
            elementMask.position.y = this.maskSprite.position.y;

            element.maskSprite = elementMask;
            element.addChild(elementMask);
            element.mask = elementMask;
        } else if (this.maskGraphics != null) {
            let elementMask = <ShapeElement>this.maskGraphics.clone();
            elementMask.position.x = this.maskGraphics.position.x;
            elementMask.position.y = this.maskGraphics.position.y;

            element.maskGraphics = elementMask;
            element.addChild(elementMask);
            element.mask = elementMask;
        }

        element.markAsCloned(); // must be called last before return!
        return element;
    }
}

/**
 * XML has attributes SwitchElementAttributes, BaseElementAttributes
 * @type {{STYLE: string; CASE: string}}
 */
export const SwitchElementAttributes = {
    /**
     * Property case is used for set element to some case
     * @property Case
     * @type string
     * @default null
     */
    CASE: "Case",
    CLONE: "Clone",
    DEBUG_INFO: "DebugInfo",
    DEBUG: "Debug",
    /**
     * Style holds elements as ContainerElement and also holds cases as xml nodes.
     * Style for switch element has to be instance of SwitchStyle.
     * @property Style
     * @type string
     * @default null
     */
    STYLE: "Style",
    /**
     * Multiplies timer with random number between min and max
     * @property Random
     * @type string
     * @default 1
     */
    RANDOM: "Random",
    /**
     * Property to select random case from array defined as: "Case1;Case2;..."
     * @property RandomCase
     * @type string
     */
    RANDOM_CASE: "RandomCase",
    DYNAMIC_TIME: "DynamicTime",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/SwitchElement.ts
 **/